import React, { useEffect, useRef, useState } from 'react';
import { TagInputProps, TagInputTypes } from './TagInput.props';
import './TagInput.styles.css';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  styled,
  TextField,
  Tooltip, tooltipClasses,
  TooltipProps,
  Typography
} from "@mui/material";
import {Clear} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { EXCEPTION_TYPE_NAMES } from "../../../constants/contants";
import { isValidTagValue } from "../../../utils/common";

const CASE_INSENSITIVE_TYPES = [TagInputTypes.DOMAIN, TagInputTypes.IP, TagInputTypes.EMAIL, TagInputTypes.URL, TagInputTypes.FILEHASH];

const TagTooltip = styled(({ className, ...props }: TooltipProps) => (
      <Tooltip className={'tag-custom-tooltip'} {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      alignItems: "center",
      maxWidth: "680px",
      borderRadius: "4px",
      boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
      fontSize: '13px',
      fontWeight: '500',
      padding: '8px',
      marginLeft: '54px',
    },
  }));

const Tags:any = ({ data, handleDelete }:any) => {
    return (
        <Box
            sx={{
                background: "#E7EBF0",
                height: "100%",
                display: "flex",
                padding: "2px 12px 2px 16px",
                margin: "4px 8px 4px 0",
                justifyContent: "center",
                alignContent: "center",
                borderRadius: "24px",
                color: "#011111",
            }}
        >
            <Stack direction='row' gap={1}>
                <TagTooltip placement="top" title={data}>
                    <Typography>{data}</Typography>
                </TagTooltip>
                <Clear
                    className={'clear-tags'}
                    sx={{ cursor: "pointer", width: "17px"}}
                    onClick={/* istanbul ignore next */() => {
                        /* istanbul ignore next */
                        handleDelete(data);
                    }}
                />
            </Stack>
        </Box>
    );
};

export const TagInput: React.FC<TagInputProps> = ({
    tags = [],
    handleModifyTags,
    tagRef,
    type = '',
    paramSendMail,
    setParamSendMail,
    checkboxInput,
    validateToField,
    setValidateToField,
    disabled=false,
    focusHandler,
    changeHandler,
    placeholder,
    errorMsg,
    error,
    setError,
    inputRows,
    maxTags,
    maxInputLength,
    inputValue = '',
    setInputValue,
}) => {
    const [checked, setChecked] = useState(() => !!(paramSendMail && paramSendMail['is_include_reporter']));
    const [inputHasError, setInputHasError] = useState(false);
    const [focus, setFocus] = useState(false);
    const [maxLenError, setMaxLenError] = useState(false);
    const { t } = useTranslation();
    const tagsContainerRef = useRef(null);
    const inputError = setError ? error : inputHasError;
    const setInputError = setError || setInputHasError;

    const maxTagsReached = !!maxTags && tags.length === maxTags;

    const scrollTags = () => {
      const el = tagsContainerRef.current as any;
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    };

    const handleDelete = (value:any) => {
        if (maxTagsReached) {
          scrollTags();
        }
        setFocus(true)
        const newTags = tags.filter((val:any) => val !== value);
        handleModifyTags?.(type, newTags);
        if(value === paramSendMail?.reportedBy){
            setChecked(!checked)
        }
        let item:any;
        if (type === TagInputTypes.TO || type === TagInputTypes.CC){
            item = [...newTags]
        }
        setParamSendMail?.({
            ...paramSendMail,
            [type]: item,
        })
    };

    const addTag = (lowercaseEmail: string) => {
        handleModifyTags?.(type, [...tags, lowercaseEmail]);
        setInputError(false);
        scrollTags();
    }

    const handleOnSubmitTag = (e:any) => {
        e.preventDefault();
        if (type !== '') {
          const rawValue = tagRef?.current.value.trim() || '';
          const inputValue = CASE_INSENSITIVE_TYPES.includes(type) ? rawValue : rawValue.toLowerCase()
          const newTags = tags?.filter((val: any) => val === inputValue);
          const validCheck = isValidTagValue(type, inputValue);

          /* istanbul ignore next */
          if (validCheck) {
            /* istanbul ignore next */
            if (newTags.length === 0) {
              addTag(inputValue);
              tagRef.current.value = "";
              setInputValue?.('');
              // scrollTags();
            } /* istanbul ignore next */ else {
              /* istanbul ignore next */
              setInputError(false);
              /* istanbul ignore next */
              tagRef.current.value = "";
              setInputValue?.('');
            }
          } else {
            if (!tags?.length && setValidateToField) {
              setValidateToField(true);
            }
          }
        }
    };

    const handleCheckNull = () => {
        if (type === 'to'){
            /* istanbul ignore next */
            if(focus && tags.length < 1){
                if (setValidateToField){
                    setValidateToField(true);
                }
            } else {
                if (setValidateToField){
                    setValidateToField(false);
                }
            }
        }
    }

    useEffect(()=>{
        handleCheckNull();
        const newTags = paramSendMail ? tags.filter((val:any) => val === paramSendMail['reportedBy']) : tags;
        setChecked(!(newTags.length === 0));
    }, [tags])

    useEffect(() => {
      const value = tagRef?.current?.value || '';
      const validCheck = isValidTagValue(type, value, true);
      const maxLenCheck = !!maxInputLength && value.length >= maxInputLength;
      setInputError(!validCheck);
      setMaxLenError(maxLenCheck);
    }, [tagRef?.current?.value]);

    const handleAddTagReporter = (e:any) => {
        const status = e.target.checked
        if(status){
            if (paramSendMail.reportedBy) {
                handleModifyTags?.(type, [...tags, paramSendMail.reportedBy]);
                setParamSendMail?.({
                    ...paramSendMail,
                    [type]: [...tags, paramSendMail.reportedBy],
                })
            }
            setChecked(true)
        } else {
            handleDelete(paramSendMail.reportedBy)
            setChecked(!checked)
        }
    }

    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleOnSubmitTag(event)
        }
    };

    const getHelperText = () => {
      if (inputError) {
        return errorMsg;
      } else {
        if (maxLenError) {
          return t('validation.charExceeded', {
          field: EXCEPTION_TYPE_NAMES[type === 'sender_domain' ? 'domain' : type],
          max: maxInputLength
        })
        } else if (maxTagsReached) {
          return t('validation.valuesLimit', { max: maxTags })
        } else if (validateToField) {
          return t('validation.required', { field: 'This field' })
        }
        return "Invalid email address or token"
      }
    };

    return (
        <>
            <Box sx={{flexGrow: 1}} className="outline-tag-textfield" id={`tag-input-${type}`}>
                <form data-testid={'tag-form'} id='tag-form' onSubmit={handleOnSubmitTag}>
                    <TextField
                        inputRef={tagRef}
                        fullWidth
                        variant='outlined'
                        size='small'
                        placeholder={!maxTagsReached ? (placeholder || "Input Email or Token and press Enter") : ''}
                        onBlur={(e) => {
                            handleOnSubmitTag(e);
                            setFocus(false)
                            changeHandler?.(type, e)
                        }}
                        onFocus={focusHandler}
                        InputProps={{
                            startAdornment: (
                                <Box sx={{margin: "0 0.2rem 0 0", display: "flex", flexWrap: "wrap"}} className={'tag-container'} ref={tagsContainerRef}>
                                    {tags.map((data: any, index: any) => {
                                        return (
                                            <Tags data={data} handleDelete={handleDelete} key={index}/>
                                        );
                                    })}
                                </Box>
                            ),
                        }}
                        inputProps={{
                          maxLength: maxInputLength,
                        }}
                        error={inputError || maxLenError || validateToField}
                        helperText={getHelperText()}
                        onKeyDown={handleKeyPress}
                        disabled={disabled || maxTagsReached}
                        multiline={!!inputRows}
                        maxRows={inputRows}
                        value={inputValue || ''}
                        onChange={(e) => {
                          if (!maxInputLength || (maxInputLength && e.target.value.length <= maxInputLength)) {
                            setInputValue?.(e.target.value);
                          }
                          changeHandler?.(type, e)
                        }}
                        className={maxTagsReached ? 'max-tags-reached' : ''}
                    />
                </form>
            </Box>
            {checkboxInput ? <FormControlLabel sx={{marginTop: '8px'}} control={<Checkbox checked={checked} disabled={disabled} onChange={(e:any) => handleAddTagReporter(e)}/>} label="Include original reporter"/> : null}
        </>
    );
};
